import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import PageHero from '@/components/common/PageHero';
import { toast } from 'sonner';
import { ticketService, type Ticket, type TicketMessage, type TicketStatus, type TicketPriority } from '@/lib/services/ticket.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Caixa } from '@/construtor/caixa';

const statusColors: Record<TicketStatus, string> = {
  OPEN: 'bg-primary',
  IN_PROGRESS: 'bg-warning',
  AWAITING_CUSTOMER: 'bg-warning',
  RESOLVED: 'bg-success',
  CLOSED: 'bg-muted',
};

const statusLabels: Record<TicketStatus, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em Andamento',
  AWAITING_CUSTOMER: 'Aguardando Resposta',
  RESOLVED: 'Resolvido',
  CLOSED: 'Fechado',
};

const priorityLabels: Record<TicketPriority, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

function getAvatarUrl(nick: string, gameType?: string): string {
  const safe = encodeURIComponent(nick.trim());
  if (!safe) return '';
  if (gameType === 'MINECRAFT') return `/api/v1/minecraft/avatar/${safe}?size=64`;
  return `https://hyvatar.io/render/${safe}?size=64`;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { config } = useTheme();
  const { store } = useStore();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    if (store?.id && isAuthenticated && ticketId) {
      loadTicketDetails();
    }
  }, [store?.id, isAuthenticated, ticketId]);

  const loadTicketDetails = async () => {
    if (!store?.id || !ticketId) return;
    try {
      setLoading(true);
      const data = await ticketService.getTicketDetails(store.id, ticketId);
      setTicket(data.ticket);
      setMessages(data.messages);
    } catch (error: any) {
      console.error('Error loading ticket details:', error);
      toast.error('Erro ao carregar detalhes do ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!store?.id || !ticketId || !replyMessage.trim()) return;

    try {
      setSending(true);
      await ticketService.replyToTicket(store.id, ticketId, {
        message: replyMessage,
      });
      setReplyMessage('');
      toast.success('Resposta enviada');
      loadTicketDetails();
    } catch (error: any) {
      console.error('Error sending reply:', error);
      toast.error('Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div>
        <PageHero
          title="Ticket"
          breadcrumbs={[
            { label: 'Suporte', href: '/suporte' },
            { label: 'Ticket' },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                Você precisa estar logado para acessar os tickets
              </p>
              <Button className="mt-4" onClick={openLoginModal}>
                Fazer Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageHero
          title="Carregando..."
          breadcrumbs={[
            { label: 'Suporte', href: '/suporte' },
            { label: 'Ticket' },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-6">
              <p>Carregando detalhes do ticket...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div>
        <PageHero
          title="Ticket não encontrado"
          breadcrumbs={[
            { label: 'Suporte', href: '/suporte' },
            { label: 'Ticket' },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                Este ticket não existe ou você não tem permissão para visualizá-lo.
              </p>
              <Button className="mt-4" onClick={() => navigate('/suporte')}>
                Voltar para Suporte
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title={ticket.title}
        breadcrumbs={[
          { label: 'Suporte', href: '/suporte' },
          { label: 'Tickets', href: '/suporte' },
          { label: `#${ticket.id.slice(0, 8)}` },
        ]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <Caixa id="suporte.ticket.voltar">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/suporte')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </div>
        </Caixa>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket Details Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Caixa id="suporte.ticket.detalhes" miolo>
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-display">Detalhes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">ID</Label>
                  <p className="font-mono text-sm">#{ticket.id.slice(0, 8)}</p>
                </div>

                <Separator />

                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge className={statusColors[ticket.status]}>
                      {statusLabels[ticket.status]}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Prioridade</Label>
                  <div className="mt-1">
                    <Badge variant="outline">
                      {priorityLabels[ticket.priority]}
                    </Badge>
                  </div>
                </div>

                <Separator />

                <div>
                  <Label className="text-muted-foreground">Criado em</Label>
                  <p className="text-sm">
                    {format(new Date(ticket.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                      locale: ptBR,
                    })}
                  </p>
                </div>

                {ticket.closedAt && (
                  <div>
                    <Label className="text-muted-foreground">Fechado em</Label>
                    <p className="text-sm">
                      {format(new Date(ticket.closedAt), "dd/MM/yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            </Caixa>
          </div>

          {/* Messages Thread */}
          <div className="lg:col-span-2 space-y-4">
            <Caixa id="suporte.ticket.conversa" miolo>
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-display">{ticket.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Messages */}
                <Caixa id="suporte.ticket.mensagens">
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {messages.map((message) => (
                    <Caixa id="suporte.ticket.mensagem" repetida key={message.id}>
                    <div
                      key={message.id}
                      className={`flex gap-3 ${message.isStaff ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      {/* Avatar */}
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <Avatar className="h-9 w-9">
                          {!message.isStaff && message.authorNick && (
                            <AvatarImage
                              src={getAvatarUrl(message.authorNick, store?.gameType)}
                              alt={message.authorNick}
                            />
                          )}
                          <AvatarFallback className={message.isStaff ? 'bg-primary/20 text-primary' : 'bg-muted-foreground/20'}>
                            {message.isStaff ? (
                              <ShieldCheck className="h-4 w-4 text-primary" />
                            ) : (
                              <span className="text-xs font-semibold">
                                {getInitials(message.authorName)}
                              </span>
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-[10px] text-muted-foreground font-medium leading-none">
                          {message.isStaff ? message.authorName : (message.authorNick ?? message.authorName)}
                        </span>
                      </div>

                      {/* Bubble */}
                      <div
                        className={`flex-1 space-y-1 ${
                          message.isStaff ? 'text-right' : 'text-left'
                        }`}
                      >
                        <div
                          className={`inline-block p-3 rounded-lg ${
                            message.isStaff
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(message.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </div>
                    </Caixa>
                  ))}
                </div>
                </Caixa>

                <Separator />

                {/* Reply Form */}
                {!ticket.isClosed && (
                  <Caixa id="suporte.ticket.responder">
                  <form onSubmit={handleSendReply} className="space-y-4">
                    <div>
                      <Label htmlFor="reply">Sua resposta</Label>
                      <Textarea
                        id="reply"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Digite sua resposta..."
                        rows={4}
                        disabled={sending}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={sending || !replyMessage.trim()}
                        className="bg-primary hover:bg-primary-hover text-primary-foreground"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {sending ? 'Enviando...' : 'Enviar Resposta'}
                      </Button>
                    </div>
                  </form>
                  </Caixa>
                )}

                {ticket.isClosed && (
                  <Caixa id="suporte.ticket.fechado">
                  <div className="text-center py-4 text-muted-foreground">
                    <p>Este ticket está fechado e não aceita mais respostas.</p>
                  </div>
                  </Caixa>
                )}
              </CardContent>
            </Card>
            </Caixa>
          </div>
        </div>
      </div>
    </div>
  );
}
